import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Phone,
  PhoneCall,
  Plus,
  Play,
  Pause,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  Bell,
  MessageSquare,
  Megaphone,
  ClipboardList,
  AlertCircle,
  RefreshCw,
  Upload,
  Download
} from 'lucide-react';
import { employeeCommunicationsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

// Pipeline options for outbound calls
const PIPELINE_OPTIONS = [
  { value: 'layer1', label: 'Layer 1 (OpenAI Realtime)', description: 'Low latency, OpenAI voices' },
  { value: 'layer2', label: 'Layer 2 (Claude + Cartesia)', description: 'Claude AI with Cartesia TTS' },
  { value: 'layer3', label: 'Layer 3 (Claude + ElevenLabs)', description: 'Claude AI with ElevenLabs voices' },
  { value: 'layer4', label: 'Layer 4 (Realtime + ElevenLabs)', description: 'Best transcription + ethnic voices' },
  { value: 'retell', label: 'Retell AI', description: 'Enterprise-grade voice AI with outbound calling' }
];

// Voice options organized by pipeline
const VOICE_OPTIONS_BY_PIPELINE = {
  layer1: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'ash', label: 'Ash' },
    { value: 'coral', label: 'Coral' },
    { value: 'echo', label: 'Echo' },
    { value: 'sage', label: 'Sage' },
    { value: 'shimmer', label: 'Shimmer' }
  ],
  layer2: [
    { value: 'a0e99841-438c-4a64-b679-ae501e7d6091', label: 'Barbershop Man' },
    { value: '79a125e8-cd45-4c13-8a67-188112f4dd22', label: 'British Lady' },
    { value: 'c2ac25f9-ecc4-4f56-9095-651354df60c0', label: 'Commercial Man' },
    { value: '5345cf08-6f37-424d-a5d9-8ae1571e1d1a', label: 'Helpful Woman' }
  ],
  layer3: [
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  layer4: [
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'MEiBY6lrwud0dREUa5FQ', label: 'Brian (African American Radio Jockey)' },
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  retell: [
    { value: '11labs-Adrian', label: 'Adrian (Male)' },
    { value: '11labs-Rachel', label: 'Rachel (Female)' },
    { value: '11labs-Drew', label: 'Drew (Male)' },
    { value: '11labs-Clyde', label: 'Clyde (Male)' },
    { value: '11labs-Paul', label: 'Paul (Male)' },
    { value: '11labs-Domi', label: 'Domi (Female)' },
    { value: '11labs-Dave', label: 'Dave (Male)' },
    { value: '11labs-Fin', label: 'Fin (Male)' },
    { value: '11labs-Sarah', label: 'Sarah (Female)' },
    { value: '11labs-Antoni', label: 'Antoni (Male)' },
    { value: '11labs-Thomas', label: 'Thomas (Male)' },
    { value: '11labs-Charlie', label: 'Charlie (Male)' },
    { value: '11labs-Emily', label: 'Emily (Female)' },
    { value: '11labs-Elli', label: 'Elli (Female)' },
    { value: '11labs-Callum', label: 'Callum (Male)' }
  ]
};

const DEFAULT_VOICE_BY_PIPELINE = {
  layer1: 'coral',
  layer2: 'a0e99841-438c-4a64-b679-ae501e7d6091',
  layer3: 'YjlcD3XHztjJEo2wNszv',
  layer4: '9wYX8b0wRvLUEYtGuzP5',
  retell: '11labs-Adrian'
};

// Pre-built communication templates
const COMMUNICATION_TEMPLATES = [
  {
    id: 'schedule_update',
    name: 'Schedule Update',
    icon: Calendar,
    description: 'Notify employees about schedule changes',
    introMessage: 'Hi {name}, this is an automated call from {company} regarding your work schedule.',
    questions: ['Have you received the updated schedule via email?', 'Do you have any conflicts with the new schedule?'],
    closingMessage: 'Thank you for confirming. If you have any questions, please contact your supervisor.'
  },
  {
    id: 'company_announcement',
    name: 'Company Announcement',
    icon: Megaphone,
    description: 'Deliver important company news',
    introMessage: 'Hi {name}, this is an important announcement from {company}.',
    questions: ['Do you have any questions about this announcement?'],
    closingMessage: 'Thank you for your time. Please check your email for more details.'
  },
  {
    id: 'shift_confirmation',
    name: 'Shift Confirmation',
    icon: ClipboardList,
    description: 'Confirm upcoming shift availability',
    introMessage: 'Hi {name}, this is {company} calling to confirm your upcoming shift.',
    questions: ['Can you confirm you will be available for your scheduled shift?', 'Do you need any accommodations?'],
    closingMessage: 'Great, thank you for confirming. See you then!'
  },
  {
    id: 'emergency_notification',
    name: 'Emergency Notification',
    icon: AlertCircle,
    description: 'Urgent notifications requiring immediate attention',
    introMessage: 'Hi {name}, this is an urgent message from {company}.',
    questions: ['Have you understood this message?', 'Do you need any immediate assistance?'],
    closingMessage: 'Please take appropriate action immediately. Contact your supervisor if needed.'
  },
  {
    id: 'policy_update',
    name: 'Policy Update',
    icon: Bell,
    description: 'Inform employees about policy changes',
    introMessage: 'Hi {name}, this is {company} with an important policy update.',
    questions: ['Do you have any questions about this policy change?'],
    closingMessage: 'Thank you. Please review the full policy document sent to your email.'
  },
  {
    id: 'custom',
    name: 'Custom Message',
    icon: MessageSquare,
    description: 'Create your own custom communication',
    introMessage: '',
    questions: [],
    closingMessage: ''
  }
];

const EmployeeCommunicationsPage = () => {
  const [activeTab, setActiveTab] = useState('employees'); // employees, campaigns, history
  const [employees, setEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Employee form
  const [employeeForm, setEmployeeForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    department: '',
    position: '',
    employeeId: ''
  });

  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    templateId: '',
    introMessage: '',
    questions: [''],
    closingMessage: '',
    pipeline: 'layer1',
    voiceModel: 'coral',
    scheduledFor: '',
    priority: 'normal'
  });

  useEffect(() => {
    loadEmployees();
    loadCampaigns();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeCommunicationsAPI.getEmployees();
      setEmployees(response.data?.data?.employees || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      // Use empty array on error
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await employeeCommunicationsAPI.getCampaigns();
      setCampaigns(response.data?.data?.campaigns || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setCampaigns([]);
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await employeeCommunicationsAPI.updateEmployee(editingEmployee.id, employeeForm);
      } else {
        await employeeCommunicationsAPI.createEmployee(employeeForm);
      }
      setShowEmployeeModal(false);
      resetEmployeeForm();
      loadEmployees();
    } catch (error) {
      console.error('Failed to save employee:', error);
      alert('Failed to save employee. Please try again.');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!confirm('Are you sure you want to delete this employee?')) return;
    try {
      await employeeCommunicationsAPI.deleteEmployee(id);
      loadEmployees();
    } catch (error) {
      console.error('Failed to delete employee:', error);
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      department: '',
      position: '',
      employeeId: ''
    });
    setEditingEmployee(null);
  };

  const openEditEmployee = (employee) => {
    setEmployeeForm({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      phone: employee.phone || '',
      email: employee.email || '',
      department: employee.department || '',
      position: employee.position || '',
      employeeId: employee.employeeId || ''
    });
    setEditingEmployee(employee);
    setShowEmployeeModal(true);
  };

  const handleTemplateSelect = (template) => {
    setCampaignForm(prev => ({
      ...prev,
      templateId: template.id,
      introMessage: template.introMessage,
      questions: template.questions.length > 0 ? template.questions : [''],
      closingMessage: template.closingMessage
    }));
  };

  const handlePipelineChange = (newPipeline) => {
    setCampaignForm(prev => ({
      ...prev,
      pipeline: newPipeline,
      voiceModel: DEFAULT_VOICE_BY_PIPELINE[newPipeline]
    }));
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }
    try {
      await employeeCommunicationsAPI.createCampaign({
        ...campaignForm,
        employeeIds: selectedEmployees
      });
      setShowCampaignModal(false);
      setSelectedEmployees([]);
      setCampaignForm({
        name: '',
        templateId: '',
        introMessage: '',
        questions: [''],
        closingMessage: '',
        pipeline: 'layer1',
        voiceModel: 'coral',
        scheduledFor: '',
        priority: 'normal'
      });
      loadCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      alert('Failed to create campaign. Please try again.');
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await employeeCommunicationsAPI.startCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to start campaign:', error);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await employeeCommunicationsAPI.pauseCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to pause campaign:', error);
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(e => e.id));
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      emp.firstName?.toLowerCase().includes(searchLower) ||
      emp.lastName?.toLowerCase().includes(searchLower) ||
      emp.department?.toLowerCase().includes(searchLower) ||
      emp.phone?.includes(searchTerm)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Employee Communications</h1>
        <p className="text-gray-600">Manage internal employee communications and announcements</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'employees', label: 'Employees', icon: Users },
            { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
            { id: 'history', label: 'History', icon: Clock }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div>
          {/* Actions Bar */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              {selectedEmployees.length > 0 && (
                <span className="text-sm text-purple-600 font-medium">
                  {selectedEmployees.length} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedEmployees.length > 0 && (
                <button
                  onClick={() => setShowCampaignModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <PhoneCall className="w-4 h-4" />
                  Call Selected ({selectedEmployees.length})
                </button>
              )}
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
              <button
                onClick={() => {
                  resetEmployeeForm();
                  setShowEmployeeModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <UserPlus className="w-4 h-4" />
                Add Employee
              </button>
            </div>
          </div>

          {/* Employees Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
                        onChange={selectAllEmployees}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Contacted
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        {employees.length === 0 ? (
                          <div>
                            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <p className="font-medium">No employees yet</p>
                            <p className="text-sm">Add employees to start sending communications</p>
                          </div>
                        ) : (
                          'No employees match your search'
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredEmployees.map(employee => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(employee.id)}
                            onChange={() => toggleEmployeeSelection(employee.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </div>
                          {employee.email && (
                            <div className="text-sm text-gray-500">{employee.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {employee.phone}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {employee.department || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {employee.position || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {employee.lastContactedAt
                            ? new Date(employee.lastContactedAt).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditEmployee(employee)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEmployee(employee.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Active Campaigns</h2>
            <button
              onClick={() => {
                if (employees.length === 0) {
                  alert('Please add employees first');
                  return;
                }
                setActiveTab('employees');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>

          {campaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="font-medium text-gray-900">No campaigns yet</p>
              <p className="text-sm text-gray-500">Select employees and create a campaign to get started</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {campaigns.map(campaign => (
                <div key={campaign.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {campaign.totalEmployees} employees • {campaign.completed}/{campaign.totalEmployees} completed
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(campaign.status))}>
                        {campaign.status}
                      </span>
                      {campaign.status === 'active' ? (
                        <button
                          onClick={() => handlePauseCampaign(campaign.id)}
                          className="p-2 text-gray-400 hover:text-yellow-600"
                        >
                          <Pause className="w-5 h-5" />
                        </button>
                      ) : campaign.status === 'paused' || campaign.status === 'draft' ? (
                        <button
                          onClick={() => handleStartCampaign(campaign.id)}
                          className="p-2 text-gray-400 hover:text-green-600"
                        >
                          <Play className="w-5 h-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${(campaign.completed / campaign.totalEmployees) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-4 flex gap-4 text-sm">
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {campaign.confirmed || 0} confirmed
                    </div>
                    <div className="flex items-center gap-1 text-red-600">
                      <XCircle className="w-4 h-4" />
                      {campaign.noAnswer || 0} no answer
                    </div>
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" />
                      {campaign.pending || 0} pending
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">
            Communication history will appear here
          </p>
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingEmployee ? 'Edit Employee' : 'Add Employee'}
            </h2>
            <form onSubmit={handleEmployeeSubmit}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={employeeForm.firstName}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={employeeForm.lastName}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={employeeForm.phone}
                    onChange={(e) => setEmployeeForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={employeeForm.email}
                    onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      value={employeeForm.department}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, department: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position
                    </label>
                    <input
                      type="text"
                      value={employeeForm.position}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, position: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={employeeForm.employeeId}
                    onChange={(e) => setEmployeeForm(prev => ({ ...prev, employeeId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Optional internal ID"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmployeeModal(false);
                    resetEmployeeForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingEmployee ? 'Save Changes' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8 p-6">
            <h2 className="text-xl font-semibold mb-4">Create Communication Campaign</h2>
            <p className="text-sm text-gray-500 mb-6">
              Calling {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''}
            </p>

            <form onSubmit={handleCreateCampaign}>
              {/* Template Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Template
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {COMMUNICATION_TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleTemplateSelect(template)}
                      className={clsx(
                        'p-3 border rounded-lg text-left hover:border-purple-500 transition-colors',
                        campaignForm.templateId === template.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200'
                      )}
                    >
                      <template.icon className={clsx(
                        'w-5 h-5 mb-2',
                        campaignForm.templateId === template.id ? 'text-purple-600' : 'text-gray-400'
                      )} />
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., January Schedule Update"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Introduction Message *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={campaignForm.introMessage}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, introMessage: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Hi {name}, this is..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Use {'{name}'} for employee name, {'{company}'} for company name</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Questions
                  </label>
                  {campaignForm.questions.map((q, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                          const newQuestions = [...campaignForm.questions];
                          newQuestions[idx] = e.target.value;
                          setCampaignForm(prev => ({ ...prev, questions: newQuestions }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder={`Question ${idx + 1}`}
                      />
                      {campaignForm.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newQuestions = campaignForm.questions.filter((_, i) => i !== idx);
                            setCampaignForm(prev => ({ ...prev, questions: newQuestions }));
                          }}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setCampaignForm(prev => ({ ...prev, questions: [...prev.questions, ''] }))}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    + Add Question
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Closing Message
                  </label>
                  <textarea
                    rows={2}
                    value={campaignForm.closingMessage}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, closingMessage: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Voice Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pipeline
                    </label>
                    <select
                      value={campaignForm.pipeline}
                      onChange={(e) => handlePipelineChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {PIPELINE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice
                    </label>
                    <select
                      value={campaignForm.voiceModel}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, voiceModel: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {VOICE_OPTIONS_BY_PIPELINE[campaignForm.pipeline]?.map(voice => (
                        <option key={voice.value} value={voice.value}>{voice.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={campaignForm.priority}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCampaignModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create & Start Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold mb-4">Import Employees</h2>
            <p className="text-sm text-gray-500 mb-4">
              Upload a CSV file with columns: firstName, lastName, phone, email, department, position
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Drag and drop or click to upload</p>
              <input type="file" accept=".csv" className="hidden" />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeCommunicationsPage;
